import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  BroadcastEventType,
  ResourceType,
  isArchivedResourceOutput,
} from '@cotera/api';
import {
  datasetRowIdColumn,
  isInternalDatasetColumn,
  resolveDatasetRowKeySpec,
} from '@cotera/sdk/core';
import type {
  BroadcastEventPayloadMap,
  ChatTriggerSource,
  ResourceUpdatedPayload,
  ResourceUpdatedPayloadChat,
  ResourceUpdatedPayloadFolder,
} from '@cotera/api';
import type {
  ResourceListAgent,
  ResourceListDataset,
  ResourceListDocument,
  ResourceListFolder,
  ResourceListWorkflow,
  TenantedClient,
  ToolListItem,
} from '@cotera/client/app/etc/api/typed-client';
import {
  EventWatchable,
  QueryClientEventWatchable,
  type QueryClientEventWatchableOptions,
  type QueryClientEventWatchableQueryKeys,
  type EventWatchableOptions,
  type ReadonlyEventWatchable,
  type WatchableGetter,
} from '@cotera/client/v0/actions/framework';
import {
  invalidateQueriesForCreditBalanceUpdated,
  invalidateQueriesForRecentChatsUpdated,
  invalidateQueriesForResourceUpdated,
  invalidateQueriesForToolCredentialUpdated,
  isStreamingChatUpdate,
} from '@cotera/client/app/hooks/broadcast-resource-query-invalidation';
import {
  queryKeys,
  RESOURCE_LIST_FETCH_STALE_MS,
} from '@cotera/client/app/hooks/query-cache-keys';
import {
  toAgentApiData,
  toAgentResourceApiData,
  type AgentResourceApiData,
} from '../agent/use-agent-query';
import {
  isCocoMentionResourceEvent,
  isDatasetResourceEvent,
  isWorkflowContentEvent,
  isWorkflowResourceEvent,
  type ResourceUpdatedFilterPayload,
} from './use-resource-updated-watchable';
import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import type { AgentNodeConfig } from '../workflow/use-agent-node-config';
import {
  agentNodeDisplayFromResourceUpdatedPayload,
  agentNodeDisplayFromWireAgent,
} from '../workflow/agent-node-wire-display';
import { datasetColumnsWithPendingOperations } from '../dataset/pending-columns';
import type { WebhookTriggerInstallState } from '../workflow/workflow-node-models/types';
import type { WebhookTriggerSettingsLoad } from '../webhook-providers/types';
import type {
  DatasetOption,
  DatasetShape,
  TriggerSummary,
  TriggerSummariesState,
  TriggerWorkflow,
  WorkflowOption,
  WorkflowTriggerOption,
} from '../trigger/hooks';

/** Resource slices backing Coco @-mention menus (`useCocoMentions` / `useAgentMentions`). */
export type CocoMentionResourcesData = {
  agents: ResourceListAgent[];
  documents: ResourceListDocument[];
  folders: ResourceListFolder[];
  datasets: ResourceListDataset[];
  workflows: ResourceListWorkflow[];
};

const COCO_MENTION_LIST_LIMIT = 100;

/**
 * Sidebar Recents refresh cadence. The list settles between turns, not between
 * tokens, so a slower trailing edge costs nothing visible and quarters the
 * ceiling on chat-list queries during a burst.
 */
const RECENT_CHATS_LIST_DEBOUNCE_MS = 2000;

/**
 * Whether a chat history list event can change what the list shows.
 *
 * `recent-chats-updated` carries no `resourceType` and always refetches: the
 * server emits it for every settled change to a chat the default list shows
 * (root, interactively started). Folder events can move chats in or out of a
 * folder view. Every other resource type cannot change a chat list at all —
 * before this, each dataset cell write and workflow save in the org refetched
 * every open history tab.
 *
 * Raw chat `resource-updated` events matter only when the list shows chats that
 * have no recents cascade (workflow, slack, chat-bot...). Every workflow run
 * produces a stream of these, so a list scoped to interactive chats ignores
 * them; the recents cascade already covers its rows.
 *
 * Chats from {@link CHAT_HISTORY_IGNORED_TRIGGER_SOURCES} never refetch,
 * whatever the filter — neither their own updates nor the folder update from
 * filing them beside their agent. Even the "Workflow runs" view only picks
 * them up on its next unrelated refresh or a reload.
 */
export function shouldRefetchChatHistoryList(
  payload: unknown,
  listsNonRecentChats: boolean
): boolean {
  if (typeof payload !== 'object' || payload === null) {
    return true;
  }
  const candidate = payload as Partial<ResourceUpdatedPayload>;
  switch (candidate.resourceType) {
    case undefined:
      return true;
    case 'folder': {
      const folder = candidate as ResourceUpdatedPayloadFolder;
      return !isIgnoredChatTriggerSource(folder.movedEntity?.triggerSource);
    }
    case 'chat': {
      const chat = candidate as ResourceUpdatedPayloadChat;
      return (
        listsNonRecentChats &&
        !isStreamingChatUpdate(chat) &&
        !isIgnoredChatTriggerSource(chat.triggerSource)
      );
    }
    default:
      return false;
  }
}

/**
 * Machine sources that create chats in bulk: warehouse peaks at thousands per
 * minute in prod, and one workflow run files a chat per sub-agent. A refetch
 * per chat would pin every open history tab.
 */
const CHAT_HISTORY_IGNORED_TRIGGER_SOURCES: ReadonlySet<ChatTriggerSource> =
  new Set(['warehouse', 'webhook', 'workflow']);

function isIgnoredChatTriggerSource(
  source: ChatTriggerSource | null | undefined
): boolean {
  return (
    source !== undefined &&
    source !== null &&
    CHAT_HISTORY_IGNORED_TRIGGER_SOURCES.has(source)
  );
}

function cocoMentionResourcesSignature(
  a: CocoMentionResourcesData,
  b: CocoMentionResourcesData
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Watchable value for the workflows-list summary batch (with load state). */
export type WorkflowSummariesState = {
  summaries: Map<string, WorkflowSummary>;
  loaded: boolean;
};

/** Watchable value for a trigger's bound-workflows list (with load state). */
export type TriggerWorkflowsState = {
  workflows: TriggerWorkflow[];
  loaded: boolean;
};

export type TriggerWorkflowsMapState = {
  workflowsByTriggerId: Map<string, TriggerWorkflow[]>;
  loaded: boolean;
};

// --- Cheap content signatures so identical event refreshes don't re-render ---

function workflowSummariesSignature(state: WorkflowSummariesState): string {
  const entries = [...state.summaries.entries()].map(
    ([id, s]) =>
      `${id}:${s.lastRunAt?.getTime() ?? ''}:${
        s.lastRunStatus ?? ''
      }:${s.triggers
        .map((t) => `${t.triggerId}/${t.sharedCount}/${t.name}`)
        .join(',')}`
  );
  return `${state.loaded}|${entries.join(';')}`;
}

function triggerSummariesSignature(map: Map<string, TriggerSummary>): string {
  return [...map.entries()]
    .map(
      ([id, s]) =>
        `${id}:${s.workflowCount}:${s.lastFiredAt?.getTime() ?? ''}:${
          s.hasError
        }:${s.workflows
          .map(
            (w) =>
              `${w.workflowId},${w.lastRunStatus ?? ''},${w.failedRuns}/${
                w.totalRuns
              }`
          )
          .join('|')}`
    )
    .join(';');
}

function triggerWorkflowsSignature(state: TriggerWorkflowsState): string {
  const rows = state.workflows.map(
    (w) =>
      `${w.workflowId}:${w.name}:${w.status}:${w.bindingEnabled}:${
        w.lastRunAt?.getTime() ?? ''
      }:${w.lastRunStatus ?? ''}:${w.folderName ?? ''}:${w.folderScope ?? ''}`
  );
  return `${state.loaded}|${rows.join(';')}`;
}

function triggerWorkflowsMapSignature(state: TriggerWorkflowsMapState): string {
  const rows = [...state.workflowsByTriggerId.entries()].map(
    ([triggerId, workflows]) =>
      `${triggerId}=${workflows
        .map(
          (w) =>
            `${w.workflowId}:${w.name}:${w.status}:${w.bindingEnabled}:${
              w.lastRunAt?.getTime() ?? ''
            }:${w.lastRunStatus ?? ''}:${w.folderName ?? ''}:${
              w.folderScope ?? ''
            }`
        )
        .join(',')}`
  );
  return `${state.loaded}|${rows.join(';')}`;
}

function triggerOptionsSignature(options: WorkflowTriggerOption[]): string {
  return options
    .map((o) => `${o.id}:${o.name}:${o.kind}:${o.schedule ?? ''}`)
    .join(';');
}

/** Per-kind label for a trigger with nothing of its own to show. */
const UNNAMED_TRIGGER_LABELS: Record<string, string> = {
  webhook: 'Webhook trigger',
  event: 'Event trigger',
  dataset: 'Dataset trigger',
};

/** What the "use existing trigger" picker calls a trigger. */
function triggerOptionName(row: {
  name?: string | null;
  cronSchedule?: string | null;
  eventName?: string | null;
  triggerKind: string;
}): string {
  // Its own name, then whatever stands in for one: a cron's schedule, an event's
  // listen name. Only then the generic per-kind label.
  const given =
    row.name ??
    row.cronSchedule ??
    (row.triggerKind === 'event' ? row.eventName : null);
  if (given !== undefined && given !== null) {
    return given;
  }
  return UNNAMED_TRIGGER_LABELS[row.triggerKind] ?? 'Trigger';
}

function datasetOptionsSignature(options: DatasetOption[]): string {
  return options.map((o) => `${o.id}:${o.name}`).join(';');
}

function datasetShapeSignature(shape: DatasetShape | null): string {
  if (shape === null) {
    return '';
  }
  return `${shape.columns.join(',')}|${shape.keyColumns.join(',')}|${
    shape.keyIsInternal
  }|${shape.rowIdColumn ?? ''}|${shape.artifactId ?? ''}`;
}

const EMPTY_AGENT_NODE_CONFIG: AgentNodeConfig = {
  tools: [],
  inputShape: null,
  editorState: null,
  agentKey: null,
  model: null,
  description: null,
};

function agentNodeConfigSignature(config: AgentNodeConfig): string {
  return `${config.agentKey ?? ''}|${JSON.stringify(config.editorState)}|${
    config.model ?? ''
  }|${config.description ?? ''}|${config.inputShape ?? ''}|${config.tools
    .map((t) => `${t.key}:${t.name}`)
    .join(',')}`;
}

function isInitialAgentNodeConfig(config: AgentNodeConfig): boolean {
  return (
    agentNodeConfigSignature(config) ===
    agentNodeConfigSignature(EMPTY_AGENT_NODE_CONFIG)
  );
}

function webhookTriggerInstallStateSignature(
  state: WebhookTriggerInstallState
): string {
  return `${state.loaded}|${state.installStatus}|${JSON.stringify(
    state.installError
  )}`;
}

function workflowOptionsSignature(options: WorkflowOption[]): string {
  return options
    .map(
      (o) => `${o.id}:${o.name}:${o.folderName ?? ''}:${o.folderScope ?? ''}`
    )
    .join(';');
}

type AgentResourceWatchableState = {
  resource: AgentResourceApiData | null;
  status: 'loading' | 'ready' | 'not-found';
};

/**
 * Whether the signed-in user can read one resource, and whether it is still
 * meant to be on screen.
 *
 * `denied` covers both NotFound and Forbidden — the backend collapses them into
 * one shape on purpose, so the client cannot tell a resource that was hard
 * deleted from one that was never shared with it, and must not pretend to.
 *
 * `archived` is a different fact and arrives by a different route: archiving
 * leaves the row readable, so the fetch succeeds and the verdict comes off
 * `status` on the body. Kept distinct from `denied` because the two want
 * opposite copy — "you can't see this" against "you put this away".
 */
export type ResourceAccessStatus =
  | 'loading'
  | 'accessible'
  | 'archived'
  | 'denied';

/** `ResourceUpdated` fields the resource-access filter reads, type-loosened so
 * the enum-vs-wire-string comparison stays a plain string compare. */
type ResourceAccessEventPayload = {
  orgId?: string;
  resourceType?: string;
  resourceId?: string;
};

const SUB_AGENT_SNAPSHOT_RECONCILE_MS = 3_000;

/**
 * Events that can change an agent node's `@`-mentioned tool status: the agent
 * itself changing (`ResourceUpdated`) or a credential being connected/removed
 * in this org (`ToolCredentialUpdated`, a local-bus event fired in the same
 * tab). Both flip which tools the agent can execute.
 */
type AgentNodeConfigWatchEvent =
  | ResourceUpdatedPayload
  | BroadcastEventPayloadMap[typeof BroadcastEventType.ToolCredentialUpdated];

export class WatchablesFactory {
  constructor(private readonly queryClient: QueryClient) {}

  private readonly _agentNodeConfigWatchables = new Map<
    string,
    ReadonlyEventWatchable<AgentNodeConfig>
  >();

  private makeEventWatchable<T, P = unknown>(
    opts: EventWatchableOptions<T, P>
  ): ReadonlyEventWatchable<T> {
    return EventWatchable.for(opts);
  }

  makeResourceUpdatedWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
  >(
    opts: Omit<EventWatchableOptions<T, P>, 'event' | 'events'>
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.ResourceUpdated,
    });
  }

  makeRecentChatsUpdatedWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.RecentChatsUpdated]
  >(
    opts: Omit<EventWatchableOptions<T, P>, 'event' | 'events'>
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.RecentChatsUpdated,
    });
  }

  makeRecentChatsListWatchable(opts: {
    client: TenantedClient;
    limit?: number;
  }): ReadonlyEventWatchable<Array<{
    id: string;
    actor_id: string;
    title: string;
    last_user_message: unknown;
    hasUnreadAssistant: boolean;
  }> | null> {
    const { client, limit = 30 } = opts;
    return this.makeRecentChatsUpdatedWatchable({
      debounceMs: RECENT_CHATS_LIST_DEBOUNCE_MS,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      runOnMount: true,
      initialValue: null,
      fn: async () => {
        const res = await client.resource.list({
          type: ResourceType.Chat,
          limit,
          // The sidebar renders rows, never a count, and the exact total costs
          // a full pass over every chat the viewer can see.
          includeTotal: false,
        });
        if (res.isErr()) {
          return [];
        }
        const out = res.value;
        if (out.type !== 'chat') {
          return [];
        }
        return out.items.map((c) => ({
          id: c.id,
          actor_id: c.actorId,
          title: c.title,
          last_user_message: c.lastMessage ?? null,
          hasUnreadAssistant: c.hasUnreadAssistant === true,
        }));
      },
    });
  }

  makeCreditBalanceUpdatedWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.CreditBalanceUpdated]
  >(
    opts: Omit<EventWatchableOptions<T, P>, 'event' | 'events'>
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.CreditBalanceUpdated,
    });
  }

  makeResourceUpdatedQuerySyncWatchable(opts: {
    orgId: string;
    queryClient: QueryClient;
  }): ReadonlyEventWatchable<number> {
    const { orgId, queryClient } = opts;
    return this.makeResourceUpdatedWatchable({
      runOnMount: false,
      initialValue: 0,
      equalityFn: () => true,
      debounceMs: 500,
      fn: async (payload) => {
        await invalidateQueriesForResourceUpdated(queryClient, orgId, payload);
        return 0;
      },
    });
  }

  makeRecentChatsUpdatedQuerySyncWatchable(opts: {
    orgId: string;
    queryClient: QueryClient;
  }): ReadonlyEventWatchable<number> {
    const { orgId, queryClient } = opts;
    return this.makeRecentChatsUpdatedWatchable({
      runOnMount: false,
      initialValue: 0,
      equalityFn: () => true,
      // Matches `makeResourceUpdatedQuerySyncWatchable`; a burst of settled
      // chat transitions should sweep the caches once, not once each.
      debounceMs: 500,
      fn: async () => {
        await invalidateQueriesForRecentChatsUpdated(queryClient, orgId);
        return 0;
      },
    });
  }

  makeCreditBalanceUpdatedQuerySyncWatchable(opts: {
    orgId: string;
    queryClient: QueryClient;
  }): ReadonlyEventWatchable<number> {
    const { orgId, queryClient } = opts;
    return this.makeCreditBalanceUpdatedWatchable({
      runOnMount: false,
      initialValue: 0,
      equalityFn: () => true,
      filter: (payload) =>
        typeof payload === 'object' &&
        payload !== null &&
        'orgId' in payload &&
        typeof payload.orgId === 'string' &&
        payload.orgId === orgId,
      fn: async (payload) => {
        await invalidateQueriesForCreditBalanceUpdated(
          queryClient,
          orgId,
          payload
        );
        return 0;
      },
    });
  }

  makeToolCredentialUpdatedWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.ToolCredentialUpdated]
  >(
    opts: Omit<EventWatchableOptions<T, P>, 'event' | 'events'>
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.ToolCredentialUpdated,
    });
  }

  makeChatBotConfigUpdatedWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.ChatBotConfigUpdated]
  >(
    opts: Omit<EventWatchableOptions<T, P>, 'event' | 'events'>
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.ChatBotConfigUpdated,
    });
  }

  /**
   * Keeps the org's tool caches fresh when a tool provider credential is added,
   * deleted, or a provider is (dis)connected. Mounted via
   * {@link CocoBroadcastQuerySyncModel}; refreshes propagate into hooks that
   * read tool data such as `useToolsAsync` / `useCocoMentions`.
   */
  makeToolCredentialUpdatedQuerySyncWatchable(opts: {
    orgId: string;
    queryClient: QueryClient;
  }): ReadonlyEventWatchable<number> {
    const { orgId, queryClient } = opts;
    return this.makeToolCredentialUpdatedWatchable({
      runOnMount: false,
      initialValue: 0,
      equalityFn: () => true,
      debounceMs: 300,
      filter: (payload) =>
        typeof payload === 'object' &&
        payload !== null &&
        'orgId' in payload &&
        typeof payload.orgId === 'string' &&
        payload.orgId === orgId,
      fn: async (payload) => {
        await invalidateQueriesForToolCredentialUpdated(
          queryClient,
          orgId,
          payload
        );
        return 0;
      },
    });
  }

  /**
   * Agents, documents, folders, datasets, and workflows for Coco
   * @-mention menus. Fetches on mount and refreshes on matching `resource-updated`
   * events (including workflow create/edit/delete, so the Workflows group stays
   * live the same way agents and documents do).
   */
  makeCocoMentionResourcesWatchable(opts: {
    orgId: string;
    client: Pick<TenantedClient, 'resource'>;
  }): ReadonlyEventWatchable<CocoMentionResourcesData | null> {
    const { orgId, client } = opts;
    let lastGood: CocoMentionResourcesData = {
      agents: [],
      documents: [],
      folders: [],
      datasets: [],
      workflows: [],
    };
    return this.makeResourceUpdatedWatchable<
      CocoMentionResourcesData | null,
      ResourceUpdatedPayload
    >({
      debounceMs: 500,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      runOnMount: true,
      initialValue: null,
      // Refetch on coco-mention resource events and on workflow *content*
      // changes (create / rename / move / archive), but NOT on workflow run or
      // trigger ticks. run-workflow.saga emits a `workflow` ResourceUpdated per
      // run status transition (changeType: 'run'); each fire would re-run all
      // five list queries below (the workflow one materializes every accessible
      // workflow and rolls up per-run counts the @-mention menu never shows), so
      // during an active event that was a firehose. isWorkflowContentEvent keeps
      // new/renamed workflows appearing immediately while dropping the ticks, and
      // its positive 'content' check keeps a mixed rollout (old server, no
      // changeType) from re-emitting the firehose.
      filter: (payload) =>
        payload.orgId === orgId &&
        (isCocoMentionResourceEvent(payload) ||
          isWorkflowContentEvent(payload)),
      equalityFn: (a, b) => {
        if (a === null || b === null) {
          return a === b;
        }
        return cocoMentionResourcesSignature(a, b);
      },
      fn: async () => {
        const limit = COCO_MENTION_LIST_LIMIT;
        const [agentRes, documentRes, folderRes, datasetRes, workflowRes] =
          await Promise.all([
            client.resource.list({ type: ResourceType.Agent, limit }),
            client.resource.list({ type: ResourceType.Document, limit }),
            client.resource.list({ type: ResourceType.Folder, limit }),
            client.resource.list({ type: ResourceType.Dataset, limit }),
            client.resource.list({
              type: ResourceType.Workflow,
              limit,
              sort: 'createdAt',
              sortDir: 'desc',
            }),
          ]);
        const data: CocoMentionResourcesData = {
          agents:
            agentRes.isOk() && agentRes.value.type === 'agent'
              ? agentRes.value.items
              : lastGood.agents,
          documents:
            documentRes.isOk() && documentRes.value.type === 'document'
              ? documentRes.value.items
              : lastGood.documents,
          folders:
            folderRes.isOk() && folderRes.value.type === 'folder'
              ? folderRes.value.items
              : lastGood.folders,
          datasets:
            datasetRes.isOk() && datasetRes.value.type === 'dataset'
              ? datasetRes.value.items
              : lastGood.datasets,
          workflows:
            workflowRes.isOk() && workflowRes.value.type === 'workflow'
              ? workflowRes.value.items
              : lastGood.workflows,
        };
        lastGood = data;
        return data;
      },
    });
  }

  /**
   * Shared watchable holding the org's tool list (`toolsV2.list`). Fetches on
   * mount and refreshes on tool-credential-updated events, so consumers can read
   * the value directly (via `useToolsAsync`) instead of going through the
   * react-query cache. `null` means the first load is still in flight; on a
   * refresh error the last good list is retained.
   */
  makeToolsListWatchable(opts: {
    orgId: string;
    client: Pick<TenantedClient, 'toolsV2'>;
  }): ReadonlyEventWatchable<ToolListItem[] | null> {
    const { orgId, client } = opts;
    let lastGood: ToolListItem[] = [];
    return this.makeToolCredentialUpdatedWatchable<ToolListItem[] | null>({
      debounceMs: 300,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      runOnMount: true,
      initialValue: null,
      filter: (payload) =>
        typeof payload === 'object' &&
        payload !== null &&
        'orgId' in payload &&
        typeof payload.orgId === 'string' &&
        payload.orgId === orgId,
      fn: async () => {
        const res = await client.toolsV2.list();
        if (res.isErr()) {
          return lastGood;
        }
        lastGood = res.value;
        return res.value;
      },
    });
  }

  makeStackFrameWrittenWatchable<
    T,
    P = BroadcastEventPayloadMap[typeof BroadcastEventType.StackFrameWritten]
  >(
    opts: Omit<
      EventWatchableOptions<T, P>,
      | 'event'
      | 'events'
      | 'debounceMs'
      | 'serializeRefreshes'
      | 'serializeQueueStrategy'
    >
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      event: BroadcastEventType.StackFrameWritten,
      debounceMs: 150,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
    });
  }

  makeSubAgentSnapshotWatchable<
    T,
    P =
      | BroadcastEventPayloadMap[typeof BroadcastEventType.StackFrameWritten]
      | Extract<
          BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated],
          { resourceType: 'chat' }
        >
      | BroadcastEventPayloadMap[typeof BroadcastEventType.ChatActionUpdated]
      | BroadcastEventPayloadMap[typeof BroadcastEventType.ArtifactCreated]
  >(
    opts: Omit<
      EventWatchableOptions<T, P>,
      | 'event'
      | 'events'
      | 'debounceMs'
      | 'serializeRefreshes'
      | 'serializeQueueStrategy'
    >
  ): ReadonlyEventWatchable<T> {
    return this.makeEventWatchable({
      ...opts,
      events: [
        BroadcastEventType.StackFrameWritten,
        BroadcastEventType.ResourceUpdated,
        BroadcastEventType.ChatActionUpdated,
        BroadcastEventType.ArtifactCreated,
      ],
      debounceMs: 250,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      reconcile: opts.reconcile ?? { cadence: SUB_AGENT_SNAPSHOT_RECONCILE_MS },
    });
  }

  makeAgentWatchable<T>(
    opts: Omit<
      EventWatchableOptions<
        T,
        BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
      >,
      'event' | 'filter' | 'runOnMount'
    > & {
      agentId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { agentId, ...rest } = opts;
    return this.makeResourceUpdatedWatchable<T>({
      ...rest,
      runOnMount: true,
      filter: (payload) =>
        payload?.resourceType === ResourceType.Agent &&
        payload?.resourceId === agentId,
    });
  }

  makeAgentResourceStateWatchable(opts: {
    agentId: string;
    client: Pick<TenantedClient, 'resource'>;
    initialValue: AgentResourceWatchableState;
  }): ReadonlyEventWatchable<AgentResourceWatchableState> {
    const { agentId, client, initialValue } = opts;

    const watchable: ReadonlyEventWatchable<AgentResourceWatchableState> =
      this.makeAgentWatchable<AgentResourceWatchableState>({
        agentId,
        initialValue,
        equalityFn: (a, b) =>
          a.status === b.status &&
          JSON.stringify(a.resource) === JSON.stringify(b.resource),
        fn: async (payload, get) => {
          if (
            payload !== undefined &&
            payload.resourceType === 'agent' &&
            payload.agent !== undefined
          ) {
            const previous = get(watchable);
            return {
              resource: {
                agent: toAgentApiData(payload.agent),
                meta: previous.resource?.meta ?? null,
              },
              status: 'ready',
            };
          }
          const result = await client.resource.get({
            type: ResourceType.Agent,
            id: agentId,
          });
          if (result.isErr()) {
            const appErr =
              result.error.t === 'application' ? result.error.err : null;
            if (
              appErr !== null &&
              (appErr.errorType === 'NotFound' ||
                appErr.errorType === 'Forbidden')
            ) {
              const previous = get(watchable);
              if (previous.resource !== null) {
                return { resource: previous.resource, status: 'ready' };
              }
              return { resource: null, status: 'not-found' };
            }
            return get(watchable);
          }
          if (result.value.type !== 'agent') {
            return get(watchable);
          }
          const next = toAgentResourceApiData(result.value);
          if (next === null) {
            return get(watchable);
          }
          return { resource: next, status: 'ready' };
        },
      });

    return watchable;
  }

  /**
   * Whether the signed-in user can read one resource — the probe behind the
   * chat artifact access gate.
   *
   * Reads through the shared `resource.get` query key, so an accessible
   * artifact's page pays no extra round trip, and re-probes on that resource's
   * `ResourceUpdated` events. The live re-probe is the point: an artifact
   * seeded before its row exists (a workflow the chat is about to create) or a
   * resource shared with you while the panel is open resolves on its own,
   * instead of holding the first verdict until a full page reload.
   */
  makeResourceAccessWatchable(opts: {
    orgId: string;
    type: ResourceType;
    id: string;
    client: Pick<TenantedClient, 'resource'>;
  }): ReadonlyEventWatchable<ResourceAccessStatus> {
    const { orgId, type, id, client } = opts;
    const queryClient = this.queryClient;
    const cacheKey = queryKeys.resource.get({ orgId, type, id });

    const watchable: ReadonlyEventWatchable<ResourceAccessStatus> =
      this.makeQueryClientEventWatchable<
        ResourceAccessStatus,
        ResourceAccessEventPayload
      >({
        event: BroadcastEventType.ResourceUpdated,
        queryKeys: cacheKey,
        runOnMount: true,
        initialValue: 'loading',
        debounceMs: 300,
        filter: (payload) =>
          payload.orgId === orgId &&
          payload.resourceType === type &&
          payload.resourceId === id,
        fn: async (_payload, get) => {
          const res = await queryClient.fetchQuery({
            queryKey: cacheKey,
            // Never serve cache: a NotFound written while a seeded resource was
            // still unpersisted must not decide the gate a minute later.
            staleTime: 0,
            queryFn: () => client.resource.get({ type, id }),
          });
          if (res.isOk()) {
            return isArchivedResourceOutput(res.value)
              ? 'archived'
              : 'accessible';
          }
          const appErr = res.error.t === 'application' ? res.error.err : null;
          if (
            appErr !== null &&
            (appErr.errorType === 'NotFound' ||
              appErr.errorType === 'Forbidden')
          ) {
            return 'denied';
          }
          // A transport failure says nothing about permissions — hold the
          // current verdict rather than claiming the artifact is unshared.
          return get(watchable);
        },
      });

    return watchable;
  }

  /**
   * An agent's workflow-node config — display metadata (title from editor H1),
   * `@`-mentioned toolset, and input shape.
   *
   * Event-driven, not polled. Refreshes on the agent's own `ResourceUpdated`
   * events (augmented payloads derive the title from `editorState` without a
   * refetch) and on `ToolCredentialUpdated` (the local-bus event fired when
   * this tab connects/removes a credential), so a tool's `canExecute` flips
   * without a re-save. The `fallback` still polls while the push stream is
   * down. There is deliberately no steady-state reconcile timer: a *foreign*
   * agent's owner-org credential changes aren't broadcast across orgs, so its
   * tool status can lag until the node remounts or the agent itself changes —
   * accepted staleness for the uncommon cross-org case.
   */
  makeAgentNodeConfigWatchable(opts: {
    agentId: string;
    client: Pick<TenantedClient, 'resource'>;
  }): ReadonlyEventWatchable<AgentNodeConfig> {
    const { agentId, client } = opts;
    const cached = this._agentNodeConfigWatchables.get(agentId);
    if (cached !== undefined) {
      if (isInitialAgentNodeConfig(cached.snapshot())) {
        void cached.refresh();
      }
      return cached;
    }

    // Read the current value via a self-reference rather than the watchable
    // param: the mount refresh runs inside the constructor, before `watchable`
    // is assigned, so touching it there would hit the TDZ. The holder lets the
    // closures below capture the reference and read the latest value once set.
    const self: { watchable?: ReadonlyEventWatchable<AgentNodeConfig> } = {};

    const fetchConfig = async (
      payload: AgentNodeConfigWatchEvent | undefined
    ): Promise<AgentNodeConfig> => {
      const previous = self.watchable?.snapshot() ?? EMPTY_AGENT_NODE_CONFIG;
      const ruPayload =
        payload !== undefined && 'resourceType' in payload
          ? payload
          : undefined;
      let display = agentNodeDisplayFromResourceUpdatedPayload(ruPayload);

      const needsResource = display === null;
      const [toolsRes, resourceRes] = await Promise.all([
        client.resource.agent.getMentionedToolsStatus({ id: agentId }),
        needsResource
          ? client.resource.get({
              type: ResourceType.Agent,
              id: agentId,
            })
          : Promise.resolve(null),
      ]);

      if (
        needsResource &&
        resourceRes !== null &&
        resourceRes.isOk() &&
        resourceRes.value.type === 'agent' &&
        resourceRes.value.agent !== undefined
      ) {
        display = agentNodeDisplayFromWireAgent(resourceRes.value.agent);
      }

      if (toolsRes.isErr()) {
        if (display === null) {
          return previous;
        }
        return {
          editorState: display.editorState,
          agentKey: display.agentKey,
          model: display.model,
          description: display.description,
          tools: previous.tools,
          inputShape: previous.inputShape,
        };
      }

      const { tools, inputShape } = toolsRes.value;
      return {
        editorState: display?.editorState ?? previous.editorState,
        agentKey: display?.agentKey ?? previous.agentKey,
        model: display?.model ?? previous.model,
        description: display?.description ?? previous.description,
        tools: tools.map((tool) => ({ key: tool.key, name: tool.name })),
        inputShape: inputShape !== '' ? inputShape : null,
      };
    };

    const watchable = this.makeEventWatchable<
      AgentNodeConfig,
      AgentNodeConfigWatchEvent
    >({
      events: [
        BroadcastEventType.ResourceUpdated,
        BroadcastEventType.ToolCredentialUpdated,
      ],
      initialValue: EMPTY_AGENT_NODE_CONFIG,
      runOnMount: true,
      equalityFn: (a, b) =>
        agentNodeConfigSignature(a) === agentNodeConfigSignature(b),
      debounceMs: 500,
      // `ResourceUpdated` fires org-wide, so keep only this agent's; a
      // `ToolCredentialUpdated` (no `resourceType`) always warrants a re-check.
      filter: (payload) =>
        'resourceType' in payload
          ? payload.resourceType === ResourceType.Agent &&
            payload.resourceId === agentId
          : true,
      fallback: {
        fn: () => fetchConfig(undefined),
        interval: SUB_AGENT_SNAPSHOT_RECONCILE_MS,
      },
      fn: (payload) => fetchConfig(payload),
    });
    self.watchable = watchable;
    this._agentNodeConfigWatchables.set(agentId, watchable);
    return watchable;
  }

  /** Tears down a cached agent-node config watchable when it leaves the graph. */
  releaseAgentNodeConfigWatchable(agentId: string): void {
    const watchable = this._agentNodeConfigWatchables.get(agentId);
    if (watchable === undefined) {
      return;
    }
    watchable.unsubscribe();
    this._agentNodeConfigWatchables.delete(agentId);
  }

  makeWorkflowTriggerWatchable<T>(
    opts: Omit<
      EventWatchableOptions<
        T,
        BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
      >,
      'event' | 'filter' | 'runOnMount'
    > & {
      triggerId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { triggerId, ...rest } = opts;
    return this.makeResourceUpdatedWatchable<T>({
      ...rest,
      runOnMount: true,
      filter: (payload) =>
        payload?.resourceType === ResourceType.WorkflowTrigger &&
        payload?.resourceId === triggerId,
    });
  }

  /**
   * Live webhook external-install state for a workflow trigger node. Backed by
   * `workflow_trigger` `ResourceUpdated` events (payload may include
   * `webhookInstall` after server augmentation) with a settings fetch fallback.
   */
  makeWebhookTriggerInstallStateWatchable(opts: {
    triggerId: string;
    client: Pick<TenantedClient, 'resource'>;
    initialValue: WebhookTriggerInstallState;
  }): ReadonlyEventWatchable<WebhookTriggerInstallState> {
    const { triggerId, client, initialValue } = opts;
    const watchable: ReadonlyEventWatchable<WebhookTriggerInstallState> =
      this.makeWorkflowTriggerWatchable<WebhookTriggerInstallState>({
        triggerId,
        initialValue,
        equalityFn: (a, b) =>
          webhookTriggerInstallStateSignature(a) ===
          webhookTriggerInstallStateSignature(b),
        fn: async (payload, get) => {
          if (
            payload !== undefined &&
            payload.resourceType === 'workflow_trigger' &&
            payload.webhookInstall !== undefined
          ) {
            return {
              installStatus: payload.webhookInstall.installStatus,
              installError: payload.webhookInstall.installError,
              loaded: true,
            };
          }
          const res = await client.resource.workflow.getWebhookTriggerSettings({
            triggerId,
          });
          if (res.isErr()) {
            return get(watchable);
          }
          return {
            installStatus: res.value.installStatus,
            installError: res.value.installError,
            loaded: true,
          };
        },
      });
    return watchable;
  }

  /**
   * Full webhook trigger settings for the edit panel. Backed by
   * `workflow_trigger` `ResourceUpdated` events with a settings fetch fallback.
   *
   * Carries the load state rather than the settings alone: the editor renders
   * "still loading" and "the request failed" differently, and cannot tell them
   * apart from a bare null.
   */
  makeWebhookTriggerSettingsWatchable(opts: {
    triggerId: string;
    client: Pick<TenantedClient, 'resource'>;
  }): ReadonlyEventWatchable<WebhookTriggerSettingsLoad> {
    const { triggerId, client } = opts;
    const watchable: ReadonlyEventWatchable<WebhookTriggerSettingsLoad> =
      this.makeWorkflowTriggerWatchable<WebhookTriggerSettingsLoad>({
        triggerId,
        initialValue: { status: 'loading' },
        equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
        fn: async (_payload, get) => {
          const res = await client.resource.workflow.getWebhookTriggerSettings({
            triggerId,
          });
          if (res.isOk()) {
            return { status: 'loaded', settings: res.value };
          }
          // A refresh that fails leaves the settings already on screen alone —
          // only a first load with nothing to fall back on becomes an error.
          const current = get(watchable);
          if (current.status === 'loaded') {
            return current;
          }
          const reported =
            res.error.t === 'application' ? res.error.err.message : undefined;
          return {
            status: 'error',
            message:
              reported === undefined || reported === ''
                ? 'Failed to load webhook settings'
                : reported,
          };
        },
      });
    return watchable;
  }

  makeAgentsListWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      'queryClient' | 'queryKeys' | 'event' | 'debounceMs' | 'runOnMount'
    > & {
      orgId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      queryKeys: this.getAgentsListQueryKeys(orgId),
    });
  }

  makeDocumentsListWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      'queryClient' | 'queryKeys' | 'event' | 'debounceMs' | 'runOnMount'
    > & {
      orgId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      queryKeys: this.getDocumentsListQueryKeys(orgId),
    });
  }

  makeWorkflowsListWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      'queryClient' | 'queryKeys' | 'event' | 'debounceMs' | 'runOnMount'
    > & {
      orgId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      queryKeys: this.getWorkflowsListQueryKeys(orgId),
    });
  }

  /**
   * The chat history page's list.
   *
   * Subscribes to `resource-updated` as well as `recent-chats-updated` because
   * the page can list chats the sidebar never shows (workflow, slack, chat-bot
   * trigger sources), and those never produce a recent-chats cascade.
   * `listsNonRecentChats` is read per event, so changing the page's trigger
   * filter takes effect without rebuilding the watchable. See
   * {@link shouldRefetchChatHistoryList} for which events refetch.
   *
   * Each refetch is a full access-checked list query, which for an org with
   * hundreds of thousands of foldered chats costs seconds of DB time, so the
   * trailing edge matches the sidebar's.
   */
  makeChatHistoryListWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      | 'queryClient'
      | 'queryKeys'
      | 'events'
      | 'filter'
      | 'debounceMs'
      | 'serializeRefreshes'
      | 'serializeQueueStrategy'
      | 'runOnMount'
    > & {
      orgId: string;
      /** True when the current filter shows chats outside the recents set. */
      listsNonRecentChats: () => boolean;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, listsNonRecentChats, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      events: [
        BroadcastEventType.RecentChatsUpdated,
        BroadcastEventType.ResourceUpdated,
      ],
      filter: (payload) =>
        shouldRefetchChatHistoryList(payload, listsNonRecentChats()),
      debounceMs: RECENT_CHATS_LIST_DEBOUNCE_MS,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      runOnMount: true,
      queryKeys: this.getChatHistoryListQueryKeys(orgId),
    });
  }

  makeTriggersListWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      'queryClient' | 'queryKeys' | 'event' | 'debounceMs' | 'runOnMount'
    > & {
      orgId: string;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      queryKeys: this.getTriggersListQueryKeys(orgId),
    });
  }

  // ---- Workflows / triggers summary + option watchables ----
  // These own the data-fetching + mapping for the workflows/triggers feature so
  // the consuming hooks/models stay thin — the setup lives here, in one place,
  // not inline at each call site.

  /** Batched workflow list rollups (triggers + shared count + last run). */
  makeWorkflowSummariesWatchable(opts: {
    client: TenantedClient;
    workflowIds: string[];
  }): ReadonlyEventWatchable<WorkflowSummariesState> {
    const { client, workflowIds } = opts;
    const hasIds = workflowIds.length > 0;
    return this.makeResourceUpdatedWatchable<
      WorkflowSummariesState,
      ResourceUpdatedFilterPayload
    >({
      initialValue: { summaries: new Map(), loaded: !hasIds },
      equalityFn: (a, b) =>
        workflowSummariesSignature(a) === workflowSummariesSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async () => {
        if (!hasIds) {
          return { summaries: new Map(), loaded: true };
        }
        // On error, return loaded+empty rather than throwing — a thrown
        // watchable fn never resolves a value, wedging `isLoading` at true.
        const res = await client.resource.workflow.summariesByIds({
          workflowIds,
        });
        if (res.isErr()) {
          return { summaries: new Map(), loaded: true };
        }
        const map = new Map<string, WorkflowSummary>();
        for (const s of res.value.summaries) {
          map.set(s.workflowId, {
            triggers: s.triggers,
            lastRunAt: s.lastRunAt !== null ? new Date(s.lastRunAt) : null,
            lastRunStatus: s.lastRunStatus,
          });
        }
        return { summaries: map, loaded: true };
      },
    });
  }

  /** Batched per-trigger rollups (workflow count, last fired, error state). */
  makeTriggerSummariesWatchable(opts: {
    client: TenantedClient;
    triggerIds: string[];
  }): ReadonlyEventWatchable<TriggerSummariesState> {
    const { client, triggerIds } = opts;
    const hasIds = triggerIds.length > 0;
    return this.makeResourceUpdatedWatchable<
      TriggerSummariesState,
      ResourceUpdatedFilterPayload
    >({
      initialValue: { summaries: new Map(), loaded: false },
      equalityFn: (a, b) =>
        a.loaded === b.loaded &&
        triggerSummariesSignature(a.summaries) ===
          triggerSummariesSignature(b.summaries),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async () => {
        if (!hasIds) {
          return { summaries: new Map(), loaded: true };
        }
        const res = await client.resource.workflow.triggerSummariesByIds({
          triggerIds,
        });
        if (res.isErr()) {
          // Settle to an empty-but-loaded state so the status cells stop
          // shimmering rather than hang on a fetch that will not arrive.
          return { summaries: new Map(), loaded: true };
        }
        const map = new Map<string, TriggerSummary>();
        for (const s of res.value.summaries) {
          map.set(s.triggerId, {
            workflowCount: s.workflowCount,
            lastFiredAt:
              s.lastFiredAt !== null ? new Date(s.lastFiredAt) : null,
            hasError: s.hasError,
            workflows: s.workflows.map((w) => ({
              workflowId: w.workflowId,
              name: w.name,
              lastRunStatus: w.lastRunStatus,
              failedRuns: w.failedRuns,
              totalRuns: w.totalRuns,
            })),
          });
        }
        return { summaries: map, loaded: true };
      },
    });
  }

  /**
   * Workflows bound to a trigger. Seeds from + writes through the React Query
   * cache so a collapsed→re-expanded row shows its last list immediately, and
   * forces a refetch on an actual event so live changes still land.
   */
  makeTriggerWorkflowsWatchable(opts: {
    client: TenantedClient;
    orgId: string;
    triggerId: string;
  }): ReadonlyEventWatchable<TriggerWorkflowsState> {
    const { client, orgId, triggerId } = opts;
    const queryClient = this.queryClient;
    const cacheKey = queryKeys.workflow.triggerWorkflows({ orgId, triggerId });
    return this.makeQueryClientEventWatchable<
      TriggerWorkflowsState,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      queryKeys: cacheKey,
      runOnMount: true,
      initialValue: (() => {
        const cached = queryClient.getQueryData<TriggerWorkflow[]>(cacheKey);
        return cached !== undefined
          ? { workflows: cached, loaded: true }
          : { workflows: [], loaded: false };
      })(),
      equalityFn: (a, b) =>
        triggerWorkflowsSignature(a) === triggerWorkflowsSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async (payload) => {
        const workflows = await queryClient.fetchQuery({
          queryKey: cacheKey,
          // Serve cache on mount/refresh; force a refetch on an actual event
          // so live binding/run changes still land.
          staleTime: payload === undefined ? RESOURCE_LIST_FETCH_STALE_MS : 0,
          queryFn: async (): Promise<TriggerWorkflow[]> => {
            const res = await client.resource.workflow.listWorkflowsForTrigger({
              triggerId,
            });
            if (res.isErr()) {
              return [];
            }
            return res.value.workflows.map((w) => ({
              workflowId: w.workflowId,
              name: w.name,
              status: w.status,
              bindingEnabled: w.bindingEnabled,
              lastRunAt: w.lastRunAt !== null ? new Date(w.lastRunAt) : null,
              lastRunStatus: w.lastRunStatus,
              folderName: w.folderName ?? null,
              folderScope: w.folderScope ?? null,
            }));
          },
        });
        return { workflows, loaded: true };
      },
    });
  }

  makeTriggerWorkflowsMapWatchable(opts: {
    client: TenantedClient;
    orgId: string;
    triggerIds: string[];
  }): ReadonlyEventWatchable<TriggerWorkflowsMapState> {
    const { client, orgId, triggerIds } = opts;
    const queryClient = this.queryClient;
    const cacheKeys = triggerIds.map((triggerId) =>
      queryKeys.workflow.triggerWorkflows({ orgId, triggerId })
    );
    return this.makeQueryClientEventWatchable<
      TriggerWorkflowsMapState,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      queryKeys: cacheKeys,
      runOnMount: true,
      initialValue: (() => {
        const workflowsByTriggerId = new Map<string, TriggerWorkflow[]>();
        let loaded = true;
        for (const triggerId of triggerIds) {
          const cacheKey = queryKeys.workflow.triggerWorkflows({
            orgId,
            triggerId,
          });
          const cached = queryClient.getQueryData<TriggerWorkflow[]>(cacheKey);
          if (cached === undefined) {
            loaded = false;
            workflowsByTriggerId.set(triggerId, []);
            continue;
          }
          workflowsByTriggerId.set(triggerId, cached);
        }
        return { workflowsByTriggerId, loaded };
      })(),
      equalityFn: (a, b) =>
        triggerWorkflowsMapSignature(a) === triggerWorkflowsMapSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async (payload) => {
        const rows = await Promise.all(
          triggerIds.map(async (triggerId) => {
            const workflows = await queryClient.fetchQuery({
              queryKey: queryKeys.workflow.triggerWorkflows({
                orgId,
                triggerId,
              }),
              staleTime:
                payload === undefined ? RESOURCE_LIST_FETCH_STALE_MS : 0,
              queryFn: async (): Promise<TriggerWorkflow[]> => {
                const res =
                  await client.resource.workflow.listWorkflowsForTrigger({
                    triggerId,
                  });
                if (res.isErr()) {
                  return [];
                }
                return res.value.workflows.map((w) => ({
                  workflowId: w.workflowId,
                  name: w.name,
                  status: w.status,
                  bindingEnabled: w.bindingEnabled,
                  lastRunAt:
                    w.lastRunAt !== null ? new Date(w.lastRunAt) : null,
                  lastRunStatus: w.lastRunStatus,
                  folderName: w.folderName ?? null,
                  folderScope: w.folderScope ?? null,
                }));
              },
            });
            return [triggerId, workflows] as const;
          })
        );
        return { workflowsByTriggerId: new Map(rows), loaded: true };
      },
    });
  }

  /** Existing org triggers for the builder's "use existing trigger" picker. */
  makeWorkflowTriggerOptionsWatchable(opts: {
    client: TenantedClient;
  }): ReadonlyEventWatchable<WorkflowTriggerOption[]> {
    const { client } = opts;
    return this.makeResourceUpdatedWatchable<
      WorkflowTriggerOption[],
      ResourceUpdatedFilterPayload
    >({
      initialValue: [],
      equalityFn: (a, b) =>
        triggerOptionsSignature(a) === triggerOptionsSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async () => {
        const res = await client.resource.list({
          type: ResourceType.WorkflowTrigger,
          limit: 100,
        });
        if (res.isErr() || res.value.type !== 'workflow_trigger') {
          return [];
        }
        return res.value.items.map((row) => ({
          id: row.id,
          name: triggerOptionName(row),
          kind: row.triggerKind,
          schedule: row.cronSchedule ?? null,
          eventName: row.eventName ?? null,
          datasetId: row.datasetId ?? null,
          criterion: row.datasetCriterion ?? null,
          runMode: row.datasetRunMode ?? null,
        }));
      },
    });
  }

  /** Org datasets for the "watch a dataset" trigger picker. */
  makeDatasetOptionsWatchable(opts: {
    client: TenantedClient;
  }): ReadonlyEventWatchable<DatasetOption[]> {
    const { client } = opts;
    return this.makeResourceUpdatedWatchable<
      DatasetOption[],
      ResourceUpdatedFilterPayload
    >({
      initialValue: [],
      equalityFn: (a, b) =>
        datasetOptionsSignature(a) === datasetOptionsSignature(b),
      filter: isDatasetResourceEvent,
      debounceMs: 500,
      fn: async () => {
        const res = await client.resource.list({
          type: ResourceType.Dataset,
          limit: 100,
        });
        if (res.isErr() || res.value.type !== 'dataset') {
          return [];
        }
        return res.value.items.map((row) => ({
          id: row.id,
          name: row.name,
          folderName: row.folderName ?? null,
          folderScope: row.folderScope ?? null,
        }));
      },
    });
  }

  /**
   * The output shape of every agent a workflow's graph references, keyed by
   * agent id, for the field pickers of the nodes downstream of them.
   *
   * Live rather than fetched once, for the same reason the dataset columns
   * below are: an agent's output shape is edited in another tab — the agent's
   * own artifact — and the drawer reading it stays open across that edit. A
   * cached fetch left the picker offering the fields the agent used to return
   * and refusing the ones it now does, for as long as the cache stood.
   *
   * All of them in one watchable rather than one each, because the set is the
   * graph's and changes with it: a hook cannot call a per-agent hook a varying
   * number of times.
   */
  makeAgentOutputShapesWatchable(opts: {
    agentIds: readonly string[];
    client: Pick<TenantedClient, 'resource'>;
  }): ReadonlyEventWatchable<ReadonlyMap<string, string>> {
    const { agentIds, client } = opts;
    const watched = new Set(agentIds);
    return this.makeResourceUpdatedWatchable<ReadonlyMap<string, string>>({
      initialValue: new Map(),
      runOnMount: true,
      equalityFn: (a, b) =>
        a.size === b.size && [...a].every(([id, shape]) => b.get(id) === shape),
      // Any agent in the graph, not one in particular. A payload that names no
      // resource still counts — dropping one that might be ours is how an edit
      // goes unnoticed.
      filter: (payload) =>
        payload?.resourceType === ResourceType.Agent &&
        (payload?.resourceId === undefined || watched.has(payload.resourceId)),
      debounceMs: 500,
      fn: async () => {
        const entries = await Promise.all(
          agentIds.map(async (agentId) => {
            const res = await client.resource.agent.getMentionedToolsStatus({
              id: agentId,
            });
            const shape = res.isOk() ? res.value.outputShape : null;
            return [agentId, shape] as const;
          })
        );
        return new Map(
          entries.filter(
            (entry): entry is readonly [string, string] =>
              typeof entry[1] === 'string' && entry[1] !== ''
          )
        );
      },
    });
  }

  /**
   * One dataset's columns and resolved row identity, for the dataset trigger
   * config form. Keyed on the dataset, so switching datasets rebuilds it.
   */
  makeDatasetShapeWatchable(opts: {
    client: TenantedClient;
    datasetId: string | null;
  }): ReadonlyEventWatchable<DatasetShape | null> {
    const { client, datasetId } = opts;
    return this.makeResourceUpdatedWatchable<
      DatasetShape | null,
      ResourceUpdatedFilterPayload & { resourceId?: string }
    >({
      initialValue: null,
      equalityFn: (a, b) =>
        datasetShapeSignature(a) === datasetShapeSignature(b),
      // Narrowed to this dataset, unlike the pickers above: a shape costs two
      // requests to read, and every other dataset in the org emits the same
      // event. A payload that does not name a dataset still counts — dropping
      // one that might be ours is how a change goes unnoticed.
      filter: (payload) =>
        // The type check runs first and tolerates a missing payload, so
        // reading a field after it is safe.
        isDatasetResourceEvent(payload) &&
        (payload.resourceId === undefined || payload.resourceId === datasetId),
      debounceMs: 500,
      fn: async () => {
        if (datasetId === null) {
          return null;
        }
        // Asked together because the answer is the pair: the artifact carries
        // the schema as of the last compaction, and the log carries every
        // column change made since. Either alone is out of date by minutes.
        const [res, pending] = await Promise.all([
          client.resource.get({
            type: ResourceType.Dataset,
            id: datasetId,
          }),
          client.resource.dataset.listOperations({ id: datasetId }),
        ]);
        if (res.isErr() || res.value.type !== 'dataset') {
          return null;
        }
        const published =
          res.value.dataset.artifacts?.[0]?.metadata?.schema.columns ?? [];
        // A failed operation read degrades to the published schema rather than
        // to nothing: columns minutes out of date beat no columns at all.
        const columns = pending.isErr()
          ? published
          : datasetColumnsWithPendingOperations(
              published,
              pending.value.operations.map((operation) => operation.payload)
            );
        // Same inputs the evaluator resolves identity from, so the warning the
        // form shows is the behaviour the trigger will actually have.
        const spec = resolveDatasetRowKeySpec({
          columns,
          rowKeyColumns: res.value.dataset.rowKeyColumns ?? null,
        });
        return {
          columns: columns
            .map((column) => column.name)
            .filter((name) => !isInternalDatasetColumn(name)),
          keyColumns: spec === null ? [] : [...spec.columns],
          keyIsInternal: spec?.internal ?? true,
          rowIdColumn: datasetRowIdColumn(columns),
          // The head artifact, which `get` returns newest-first — the same one
          // the published schema above was read from, so an edit written
          // against this shape names the version it actually saw.
          artifactId: res.value.dataset.artifacts?.[0]?.id ?? null,
        };
      },
    });
  }

  /** Org workflows for the "attach a workflow to this trigger" picker. */
  makeWorkflowOptionsWatchable(opts: {
    client: TenantedClient;
  }): ReadonlyEventWatchable<WorkflowOption[]> {
    const { client } = opts;
    return this.makeResourceUpdatedWatchable<
      WorkflowOption[],
      ResourceUpdatedFilterPayload
    >({
      initialValue: [],
      equalityFn: (a, b) =>
        workflowOptionsSignature(a) === workflowOptionsSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async () => {
        const res = await client.resource.list({
          type: ResourceType.Workflow,
          limit: 100,
          sort: 'createdAt',
          sortDir: 'desc',
        });
        if (res.isErr() || res.value.type !== 'workflow') {
          return [];
        }
        return res.value.items.map((w) => ({
          id: w.id,
          name: w.name,
          folderName: w.folderName ?? null,
          folderScope: w.folderScope ?? null,
        }));
      },
    });
  }

  /**
   * Standalone workflows (no trigger bound), filtered server-side via
   * `triggerKind: 'none'` so the set is complete instead of derived from the
   * (page-limited) full workflow list. Restreams on binding changes.
   */
  makeUnattachedWorkflowsWatchable(opts: {
    client: TenantedClient;
  }): ReadonlyEventWatchable<WorkflowOption[]> {
    const { client } = opts;
    return this.makeResourceUpdatedWatchable<
      WorkflowOption[],
      ResourceUpdatedFilterPayload
    >({
      initialValue: [],
      equalityFn: (a, b) =>
        workflowOptionsSignature(a) === workflowOptionsSignature(b),
      filter: isWorkflowResourceEvent,
      debounceMs: 500,
      fn: async () => {
        const res = await client.resource.list({
          type: ResourceType.Workflow,
          limit: 100,
          triggerKind: 'none',
          sort: 'createdAt',
          sortDir: 'desc',
        });
        if (res.isErr() || res.value.type !== 'workflow') {
          return [];
        }
        return res.value.items.map((w) => ({
          id: w.id,
          name: w.name,
          folderName: w.folderName ?? null,
          folderScope: w.folderScope ?? null,
        }));
      },
    });
  }

  makeFolderContentsWatchable<T, P = unknown>(
    opts: Omit<
      QueryClientEventWatchableOptions<T, P>,
      'queryClient' | 'queryKeys' | 'event' | 'debounceMs' | 'runOnMount'
    > & {
      orgId: string;
      folderId: string | null;
      getScope: (get: WatchableGetter) => 'private' | 'shared' | undefined;
    }
  ): ReadonlyEventWatchable<T> {
    const { orgId, folderId, getScope, ...rest } = opts;
    return this.makeQueryClientEventWatchable<T, P>({
      ...rest,
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      // Folder placement broadcasts are resource-scoped (owner + explicit
      // grantees), so viewers who aren't the owner can miss invalidation
      // events entirely. Refetch from the network on every refresh — including
      // mount — so opening a folder never trusts a cache the missed events
      // should have invalidated (items looked "hidden" until a sort changed
      // the query key).
      invalidatePrefixesOnEveryRefresh: true,
      queryKeys: (get) => {
        return this.getFolderContentsQueryKeys({
          orgId,
          folderId,
          scope: getScope(get),
        });
      },
    });
  }

  private getAgentsListQueryKeys(orgId: string): readonly QueryKey[] {
    return [
      [orgId, 'resource', 'listPage', ResourceType.Agent],
      queryKeys.agents.all({ orgId }),
    ];
  }

  private getDocumentsListQueryKeys(orgId: string): readonly QueryKey[] {
    return [[orgId, 'resource', 'listPage', ResourceType.Document]];
  }

  private getChatHistoryListQueryKeys(orgId: string): readonly QueryKey[] {
    return [
      [orgId, 'resource', 'listPage', ResourceType.Chat],
      [orgId, 'resource', 'folderContents'],
      queryKeys.chat.conversations({ orgId }),
      [orgId, 'resource', 'unfiledTotals'],
    ];
  }

  private getTriggersListQueryKeys(orgId: string): readonly QueryKey[] {
    return [
      [orgId, 'resource', 'listPage', ResourceType.WorkflowTrigger],
      [orgId, 'resource', 'listPage', ResourceType.Workflow],
    ];
  }

  private getWorkflowsListQueryKeys(orgId: string): readonly QueryKey[] {
    return [
      [orgId, 'resource', 'listPage', ResourceType.Workflow],
      [orgId, 'resource', 'listPage', ResourceType.WorkflowTrigger],
    ];
  }

  private getFolderContentsQueryKeys(params: {
    orgId: string;
    folderId: string | null;
    scope: 'private' | 'shared' | undefined;
  }): QueryClientEventWatchableQueryKeys {
    if (params.folderId === null) {
      // Top-level Library reads from `library-root` (the "All" view) and
      // `listPage/<type>` (single-type views). Invalidate every slice it can
      // display so a create/archive/move elsewhere is reflected immediately
      // instead of after `staleTime`. Each entry is a stable prefix; the
      // concrete keys append extra params (offset/search/sort) later.
      return [
        [params.orgId, 'resource', 'libraryRoot'],
        [params.orgId, 'resource', 'listPage', ResourceType.Folder],
        [params.orgId, 'resource', 'listPage', ResourceType.Agent],
        [params.orgId, 'resource', 'listPage', ResourceType.Document],
        [params.orgId, 'resource', 'listPage', ResourceType.Workflow],
        [params.orgId, 'resource', 'listPage', ResourceType.Dataset],
      ];
    }

    return [
      [
        params.orgId,
        'resource',
        'folderContents',
        params.folderId,
        ...(params.scope !== undefined ? [params.scope] : []),
      ],
      queryKeys.resource.get({
        orgId: params.orgId,
        type: ResourceType.Folder,
        id: params.folderId,
      }),
    ] as const;
  }

  private makeQueryClientEventWatchable<T, P = unknown>(
    opts: Omit<QueryClientEventWatchableOptions<T, P>, 'queryClient'>
  ): ReadonlyEventWatchable<T> {
    return QueryClientEventWatchable.for({
      ...opts,
      queryClient: this.queryClient,
    });
  }
}
